import { Injectable } from '@angular/core';
import { WorkspaceDatabaseKeys } from '@database/workspace';
import { DataModel, FileDataTypesMapSort, PrimaryKeyValueModel, SortingTypeEnum } from '@shared';
import { map, Observable } from 'rxjs';
import { differenceWith, isEqual, orderBy } from 'lodash';
import { SortingColumn } from '../../sorting';
import { SearchService } from '@domain/search';

@Injectable()
export class SortingService {
	public constructor(private searchService: SearchService) {}

	public getSortedChildData$(
		tableName: string,
		projectNumber: string,
		primaryKeyValue: PrimaryKeyValueModel[],
		sortingStatus: {
			direction: SortingTypeEnum;
			column: SortingColumn;
		}
	): Observable<DataModel[]> {
		return this.searchService.getSearchResults$(tableName, projectNumber, primaryKeyValue).pipe(
			map((data: DataModel[]) => {
				// filter data entries which contain the property that we filter by, so that
				// we place the difference (dataDiff) appended at the end of the filtering result.
				const dataWithSortedColumnValue = data.filter(
					(td: DataModel) => sortingStatus.column.name in td && td[sortingStatus.column.name] !== undefined
				);
				const dataDiff = differenceWith(data, dataWithSortedColumnValue, isEqual);

				return [
					...orderBy(
						dataWithSortedColumnValue,
						(entry: DataModel) =>
							sortingStatus.column.dataType === FileDataTypesMapSort.NUMERIC
								? Number(entry[sortingStatus.column.name])
								: entry[sortingStatus.column.name],
						[sortingStatus.direction]
					),
					...dataDiff
				];
			})
		);
	}

	public getSortedEntityData(
		tableName: string,
		projectNumber: string,
		sortingStatus: {
			direction: SortingTypeEnum;
			column: SortingColumn;
		}
	): Observable<DataModel[]> {
		return this.searchService.getTableData$(tableName, projectNumber).pipe(
			map((data: DataModel[]) => {
				// filter data entries which contain the property that we filter by, so that
				// we place the difference (dataDiff) appended at the end of the filtering result.
				const dataWithSortedColumnValue = data.filter(
					(td: DataModel) => sortingStatus.column.name in td && td[sortingStatus.column.name] !== undefined
				);
				const dataDiff = differenceWith(data, dataWithSortedColumnValue, isEqual);

				return [
					...orderBy(
						dataWithSortedColumnValue,
						(entry: DataModel) =>
							sortingStatus.column.dataType === FileDataTypesMapSort.NUMERIC
								? Number(entry[sortingStatus.column.name])
								: entry[sortingStatus.column.name],
						[sortingStatus.direction]
					),
					...dataDiff
				];
			})
		);
	}

	public getCombinedSortedData(
		tableName: string,
		standardTableName: string,
		projectNumber: string,
		sortingStatus: {
			direction: SortingTypeEnum;
			column: SortingColumn;
		},
		primaryKeyValue?: PrimaryKeyValueModel[]
	): Observable<DataModel[]> {
		return this.searchService
			.getCombinedSortedData$(tableName, standardTableName, projectNumber, primaryKeyValue)
			.pipe(
				map((combinedData: [DataModel[], DataModel[]]) => {
					// filter data entries which contain the property that we filter by, so that
					// we place the difference (standardsDiff) appended at the end of the filtering result.
					// Same happens for standardsWithInsertedDepth
					const samplesWithSortedColumnValue = combinedData[0].filter(
						(td: DataModel) =>
							sortingStatus.column.name in td && td[sortingStatus.column.name] !== undefined
					);
					const standardsWithInsertedDepth = combinedData[1].filter(
						(data: DataModel) =>
							WorkspaceDatabaseKeys.columns.insertedDepth in data &&
							!!data[WorkspaceDatabaseKeys.columns.insertedDepth]
					);

					const standardsDiff = differenceWith(combinedData[1], standardsWithInsertedDepth, isEqual);
					const samplesDiff = differenceWith(combinedData[0], samplesWithSortedColumnValue, isEqual);

					// in order to mix standard records with sample, I mocked the std table with depth_from and to
					// both having the same value of inserted_depth - they are deleted in table service
					const mockedStandardsWithDepths = standardsWithInsertedDepth.map(
						(mockedStandardsWithDepthsData: DataModel) => ({
							...mockedStandardsWithDepthsData,
							[WorkspaceDatabaseKeys.columns.depthFrom]: mockedStandardsWithDepthsData.inserted_depth,
							[WorkspaceDatabaseKeys.columns.depthTo]: mockedStandardsWithDepthsData.inserted_depth
						})
					);

					const mockedCombinedSamplesWithStandards = [
						...samplesWithSortedColumnValue,
						...mockedStandardsWithDepths
					];
					const combinedSamplesWithStandards = [...samplesWithSortedColumnValue, ...combinedData[1]];

					return [WorkspaceDatabaseKeys.columns.depthFrom, WorkspaceDatabaseKeys.columns.depthTo].includes(
						sortingStatus.column.name
					)
						? [
								...orderBy(
									mockedCombinedSamplesWithStandards,
									[
										(entry: DataModel) =>
											sortingStatus.column.dataType === FileDataTypesMapSort.NUMERIC
												? Number(entry[sortingStatus.column.name])
												: entry[sortingStatus.column.name],
										Number(WorkspaceDatabaseKeys.columns.insertedDepth)
									],
									[sortingStatus.direction, sortingStatus.direction]
								),
								...samplesDiff,
								...standardsDiff
						  ]
						: [
								// when choosing a totally different column than depth_from/to, keep the standard records at the
								// bottom - the catch here is that both samples and standards share rxdbid, so picking that field
								// for sorting will consider standards in grouping, so I chose standard_number for the table and 'desc'
								// so that they will be stacked at the bottom, but sorted based on std number
								...orderBy(
									combinedSamplesWithStandards,
									[
										WorkspaceDatabaseKeys.columns.standardNumber,
										(entry: DataModel) =>
											sortingStatus.column.dataType === FileDataTypesMapSort.NUMERIC
												? Number(entry[sortingStatus.column.name])
												: entry[sortingStatus.column.name]
									],
									['desc', sortingStatus.direction]
								),
								...samplesDiff
						  ];
				})
			);
	}
}
